import { css } from '@emotion/react';
import { FC, PropsWithChildren } from 'react';

import { ConditionalBlurWrapper } from '~src/blur/conditional-blur-wrapper';
import { SplitDeliveryCartManager } from '~src/cart/split-delivery/split-delivery-cart-manager';
import { CategoryManager } from '~src/category/category-manager';
import {
  useAdditionalInfoEventEmitter,
  useDataChangedEventDataEmitter,
  useNoShippingOptionsEventEmitter,
  useSplitDeliveryEventEmitter,
} from '~src/utils/event-emitters/data-event-emitter';
import { useSummaryChangedEventEmitter } from '~src/utils/event-emitters/summary-changed';

import { useHasAddress } from '~address-form/utils/address-utils';

import { CartManager } from '../cart/cart-manager';
import { FreeShippingIndicator } from '../free-shipping-indicator/free-shipping-indicator';
import { SessionStateContext, useGlobalState, useSessionState } from '../global-state';
import { useInvalidChoiceDetection } from '../utils/invalid-choice-detection';
import { CategoryManagerPlaceholder } from './category-manager-placeholder';
import { SessionHeading } from './session-heading';

const DeliveryCartProvider: FC<PropsWithChildren> = ({ children }) => {
  const { sessions } = useGlobalState();

  const isSplitDelivery = sessions.length > 1;

  if (isSplitDelivery) {
    return (
      <div
        css={theme => ({
          border: `1px solid ${theme.neutral85}`,
          padding: '16px',
          borderRadius: '8px',
        })}
      >
        <SplitDeliveryCartManager />
        {children}
      </div>
    );
  }

  return (
    <>
      <CartManager />
      {children}
    </>
  );
};

const SessionLayout: FC<PropsWithChildren> = ({ children }) => {
  return <ConditionalBlurWrapper css={styles.wrapper}>{children}</ConditionalBlurWrapper>;
};

const SessionManagerItem: FC = () => {
  const { session_id } = useSessionState();

  useDataChangedEventDataEmitter(session_id);
  useNoShippingOptionsEventEmitter(session_id);

  return (
    <SessionLayout>
      <SessionHeading />
      <FreeShippingIndicator />
      <DeliveryCartProvider>
        <CategoryManager />
      </DeliveryCartProvider>
    </SessionLayout>
  );
};

const SessionManagerList: FC = () => {
  const globalState = useGlobalState();

  useSplitDeliveryEventEmitter();

  return (
    <>
      {globalState.sessions.map(session => (
        <SessionStateContext.Provider key={session.session_id} value={session}>
          <SessionManagerItem />
        </SessionStateContext.Provider>
      ))}
    </>
  );
};

export const SessionManager: FC = () => {
  const globalState = useGlobalState();
  // TODO: move the config to global state.
  const session = globalState.sessions[0];

  const hasAddress = useHasAddress();

  useSummaryChangedEventEmitter();
  useInvalidChoiceDetection();
  useAdditionalInfoEventEmitter();

  /*
   *
   * Using recipient address is considered as different path - we're ignoring
   * the show_shipping_categories_before_address flag, because shipping options
   * will always be visible or hidden under the overlay.
   *
   */

  if (globalState.use_recipient_address) {
    return <SessionManagerList />;
  }

  const showPlaceholder = !hasAddress && !session.flags.show_shipping_categories_before_address;

  if (showPlaceholder) {
    return <CategoryManagerPlaceholder />;
  }

  return <SessionManagerList />;
};

const styles = {
  wrapper: css({
    position: 'relative',
    marginTop: '28px',
    '&:first-child': {
      // Hack: first item should not have margin.
      marginTop: '0',
    },
  }),
};
